import { cn } from "@/lib/utils/cn";
import { useId } from "react";

export interface RadioBoxOption<TValue extends string> {
  description?: string;
  label: string;
  value: TValue;
}

export function RadioBoxInput<TValue extends string>({
  ariaLabel,
  className,
  disabled = false,
  name,
  onValueChange,
  options,
  value,
}: {
  ariaLabel: string;
  className?: string;
  disabled?: boolean;
  name?: string;
  onValueChange: (value: TValue) => void;
  options: readonly RadioBoxOption<TValue>[];
  value: TValue;
}) {
  const generatedName = useId();
  const radioName = name ?? generatedName;

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn("grid grid-cols-1 gap-2 sm:grid-cols-3", className)}
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <label
            key={option.value}
            className={cn(
              "border-border hover:bg-muted/40 flex min-w-0 cursor-pointer items-start gap-2 rounded-lg border px-3 py-2 transition-colors",
              selected && "border-primary bg-primary/5 ring-primary/20 ring-1",
              disabled && "cursor-not-allowed opacity-50",
            )}
          >
            <input
              type="radio"
              className="accent-primary mt-0.5 h-4 w-4 shrink-0"
              name={radioName}
              value={option.value}
              checked={selected}
              disabled={disabled}
              onChange={() => {
                onValueChange(option.value);
              }}
            />
            <span className="min-w-0">
              <span className="block text-sm font-medium">{option.label}</span>
              {option.description ? (
                <span className="text-muted-foreground mt-0.5 block text-xs">
                  {option.description}
                </span>
              ) : null}
            </span>
          </label>
        );
      })}
    </div>
  );
}
